import { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import Api from '../../../../api/Api'
import { trackUpdate, EVENT_ANALYTICS_TYPE_UPDATED } from '../../../../utils/matomoEvents'
import { ADVANCED_ANALYTICS } from "../../../../types/analytics.d"

export const useAdvancedAnalytics = (coordinates: number[]) => {
  const [images, setImages] = useState({
    statistics: '',
    extremeValues: '',
    persistence: '',
    histogram: '',
    exceedance: '',
    tidal: ''
  })

  const [links, setLinks] = useState({
    statistics: [] as string[],
    extremeValues: [] as string[],
    persistence: [] as string[],
    histogram: [] as string[],
    exceedance: [] as string[],
    tidal: [] as string[]
  })

  const [errors, setErrors] = useState({
    statistics: '',
    extremeValues: '',
    persistence: '',
    histogram: '',
    exceedance: '',
    tidal: ''
  })
  const [advancedAnalyticType, setAdvancedAnalyticType] = useState<ADVANCED_ANALYTICS>(ADVANCED_ANALYTICS.STATISTICS);

  const advancedAnalyticsMutation = useMutation({
    mutationFn: async ({ payload, type }: { payload: any, type: ADVANCED_ANALYTICS }) => {
      setAdvancedAnalyticType(type)
      const result = await Api.chart.getAdvancedAnalytics({
        ...payload,
        PlotFlag: type === ADVANCED_ANALYTICS.HISTOGRAM ? '2' : '[1 3]',
      })
      return { result, type, payload }
    },
    onSuccess: ({ result, type, payload }) => {
      const key = getAnalyticsKey(type)

      if (result?.Result?.img) {
        const imageData = 'data:image/png;base64,' + result.Result.img
        setImages(prev => ({ ...prev, [key]: imageData }))
        setLinks(prev => ({ ...prev, [key]: result.Result.links || [] }))
        setErrors(prev => ({ ...prev, [key]: '' }))

        trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, coordinates, payload.DatasetId, type)
      } else {
        const errorMessage = result?.Error?.ErrorMessage || 'Failed to return data'
        setErrors(prev => ({ ...prev, [key]: errorMessage }))
        setImages(prev => ({ ...prev, [key]: '' }))
        setLinks(prev => ({ ...prev, [key]: [] }))
      }
    },
    onError: (error: any, { type }) => {
      const key = getAnalyticsKey(type)
      const errorMessage = error?.response?.data?.Message || 'Failed to return data'
      setErrors(prev => ({ ...prev, [key]: errorMessage }))
    }
  })

  const getAnalyticsKey = (type: ADVANCED_ANALYTICS) => {
    switch (type) {
      case ADVANCED_ANALYTICS.STATISTICS: return 'statistics'
      case ADVANCED_ANALYTICS.EXTREME_VALUES: return 'extremeValues'
      case ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW: return 'persistence'
      case ADVANCED_ANALYTICS.HISTOGRAM: return 'histogram'
      case ADVANCED_ANALYTICS.EXCEEDANCE: return 'exceedance'
      case ADVANCED_ANALYTICS.TIDAL_LEVEL: return 'tidal'
      default: return 'statistics'
    }
  }

  const updateStatistics = useCallback((payload: any) => {
    advancedAnalyticsMutation.mutate({ payload, type: ADVANCED_ANALYTICS.STATISTICS })
  }, [])

  const updateExtremeValues = useCallback((payload: any) => {
    advancedAnalyticsMutation.mutate({ payload, type: ADVANCED_ANALYTICS.EXTREME_VALUES })
  }, [])

  const updatePersistence = useCallback((payload: any) => {
    advancedAnalyticsMutation.mutate({ payload, type: ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW })
  }, [])

  const updateHistogram = useCallback((payload: any) => {
    advancedAnalyticsMutation.mutate({ payload, type: ADVANCED_ANALYTICS.HISTOGRAM })
  }, [])

  const updateExceedance = useCallback((payload: any) => {
    advancedAnalyticsMutation.mutate({ payload, type: ADVANCED_ANALYTICS.EXCEEDANCE })
  }, [])

  const updateTidalLevel = useCallback((payload: any) => {
    advancedAnalyticsMutation.mutate({ payload, type: ADVANCED_ANALYTICS.TIDAL_LEVEL })
  }, [])

  const setErrorMessage = useCallback((type: ADVANCED_ANALYTICS, errorMessage: string) => {
    const key = getAnalyticsKey(type)
    setImages(prev => ({ ...prev, [key]: '' }))
    setErrors(prev => ({ ...prev, [key]: errorMessage }))
    setLinks(prev => ({ ...prev, [key]: [] }))
  }, [])

  const resetAdvancedAnalytics = useCallback(() => {
    setImages({
      statistics: '',
      extremeValues: '',
      persistence: '',
      histogram: '',
      exceedance: '',
      tidal: ''
    })
    setLinks({
      statistics: [],
      extremeValues: [],
      persistence: [],
      histogram: [],
      exceedance: [],
      tidal: []
    })
    setErrors({
      statistics: '',
      extremeValues: '',
      persistence: '',
      histogram: '',
      exceedance: '',
      tidal: ''
    })
  }, [])

  return {
    images,
    links,
    errors,
    updateStatistics,
    updateExtremeValues,
    updatePersistence,
    updateHistogram,
    updateExceedance,
    updateTidalLevel,
    resetAdvancedAnalytics,
    advancedAnalyticType,
    setErrorMessage,
    isUpdating: advancedAnalyticsMutation.isPending,
  }
}
